import { useRef, useState } from "react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, X, Image as ImageIcon, Video } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MediaUpload({ label, accept, kind = "image", value, onChange, testid }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setProgress(0);
    try {
      const res = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0),
      });
      const url = res.data.url.startsWith("http") ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      onChange(url);
      toast.success(`${kind === "video" ? "Video" : "Photo"} uploaded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setProgress(null);
    }
  };

  const Icon = kind === "video" ? Video : ImageIcon;

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{label}</p>
      {value ? (
        <div className="relative rounded-2xl overflow-hidden border border-border bg-secondary/5" data-testid={`${testid}-preview`}>
          {kind === "video" ? (
            <video src={value} controls className="w-full h-44 object-cover bg-black" />
          ) : (
            <img src={value} alt="preview" className="w-full h-44 object-cover" />
          )}
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
            data-testid={`${testid}-remove`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={progress !== null}
          className="w-full rounded-2xl border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors py-8 flex flex-col items-center justify-center text-muted-foreground gap-2"
          data-testid={testid}
        >
          {progress !== null ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="text-sm">Uploading… {progress}%</span>
            </>
          ) : (
            <>
              <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <span className="text-sm flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Choose a {kind}</span>
              <span className="text-xs">{kind === "video" ? "MP4, MOV up to 100MB" : "JPG, PNG up to 15MB"}</span>
            </>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}
